import { Injectable } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { Amplify, AWSCloudWatchProvider, Logger } from "aws-amplify";
import {
    BaseUserService,
    ViewerService,
    MissionService,
    waitUntil,
    VisitTemplate,
    TemplateService,
    Mission,
} from "@smarterplan/ngx-smarterplan-core";
import { DateTime } from "luxon";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { RemoteControlService } from "../remote-control/remote-control.service";
import { ZoomService } from "../zoom/zoom.service";
import { APIService } from "../API.service";
import { PipeCacheService } from "../pipes/pipe-cache.service";
import { EndOfVisitModalComponent } from "./components/modals/end-of-visit-modal/end-of-visit-modal.component";
import { ChatMessageService } from "../services/chat-message.service";

@Injectable({
    providedIn: "root",
})
export class GuidedTourService {
    currentTourLive: VisitTemplate;

    tourLaunchedEvent: Subject<VisitTemplate> = new Subject();

    tourFinishedEvent: Subject<boolean> = new Subject();

    tourLaunchError: Subject<boolean> = new Subject();

    public tourToBeLaunchedDirectly: VisitTemplate;

    remoteConnectionSubscription: Subscription;

    zoomUserCountSubscription: Subscription;

    hostLeftEvent: Subject<boolean> = new Subject();

    public remoteUserCount = 0;

    public zoomUserCount = 0;

    public inFreeVisitMode = true; // if visitor is in a zoom tour but in free mode (always true for guide)

    logger: Logger;

    loggerInfo: { name: string; tourID: string; };

    isConnecting = false;

    channelSubscription: any;

    guideConnectedSubscription: Subscription;

    currentMission: Mission;

    constructor(
        private zoomService: ZoomService,
        private userService: BaseUserService,
        private viewerService: ViewerService,
        private remoteService: RemoteControlService,
        private missionService: MissionService,
        private templateService: TemplateService,
        private API: APIService,
        private pipeCacheService: PipeCacheService<{
            avatar?: string;
            pseudo?: string;
        }>,
        private modalService: NgbModal,
        private chatMessageService: ChatMessageService,
    ) { }

    async launchTour(tour: VisitTemplate) {
        this.currentMission = this.userService.currentMission(tour.spaceID);
        // -- Logging
        this.initLogger();
        this.loggerInfo = {
            name: this.userService.cu.displayName,
            tourID: tour.id.slice(0, 8),
        };
        this.zoomService.setLogger(this.logger, this.loggerInfo);
        this.zoomService.setIsGuide(true);
        // -- Zoom connection
        try {
            await this.zoomService.joinSession(this.userService.cu.id, tour.id);
        } catch (error) {
            console.log("error launch tour", error);
            this.tourLaunchError.next(false);
            return;
        }
        this.zoomUserCount = this.zoomService.getZoomUserCount();
        this.zoomUserCountSubscription =
            this.zoomService.connectedUserCount.subscribe((number) => {
                this.zoomUserCount = number;
            });
        this.currentTourLive = tour;
        this.chatMessageService.currentTourID = tour.id;
        const startDate = DateTime.now();
        this.chatMessageService.tourStartedAt = startDate.toMillis();
        // init participants (only after setting variables in chatMessageService)
        await this.zoomService.initParticipants();
        this.tourLaunchedEvent.next(tour);

        // -- Photon Remote Session
        if (this.viewerService.checkMode("Matterport")) {
            const matterportService = this.viewerService.getMatterportService();
            const { sdk } = matterportService;
            if (sdk) {
                this.remoteService.setLogger(this.logger, this.loggerInfo);
                this.remoteService.setSdk(sdk, tour, true);
                await this.remoteService.connectHost(tour.id);
            }
        }

        // update mission template
        await this.templateService.update({
            id: tour.id,
            realStartedAt: startDate.toMillis(),
            realEndedAt: null,
        });
        // subscribe to update of pseudo and avatar by other users
        this.channelSubscription = this.API.SubscribeToChannelListener(
            tour.id,
        ).subscribe({
            next: async (data) => {
                const payload = data.value.data.subscribeToChannel.data;
                const jsonReceived = JSON.parse(payload);
                if (jsonReceived.avatar) {
                    this.pipeCacheService.cache[jsonReceived.userID].avatar =
                        jsonReceived.avatar;
                }
                if (jsonReceived.pseudo) {
                    this.pipeCacheService.cache[jsonReceived.userID].pseudo =
                        jsonReceived.pseudo;
                }
            },
            error: (error) => console.error(JSON.stringify(error)),
        });
        await this.updateConnectionToLiveTime(true);
    }

    async joinTour(tour: VisitTemplate): Promise<void> {
        this.currentMission = this.userService.currentMission(tour.spaceID);
        this.initLogger();
        this.guideConnectedSubscription =
            this.zoomService.guideConnected.subscribe(async (isConnected) => {
                if (!isConnected) {
                    // the guide has disconnected not properly (F5...)
                    await this.templateService.update({
                        id: this.currentTourLive.id,
                        realEndedAt: DateTime.now().toMillis(),
                    });
                } else {
                    // guide has returned!
                    await this.rejoinRemoteSessionPhoton();
                }
            });
        this.loggerInfo = {
            name: this.userService.cu.displayName,
            tourID: tour.id.slice(0, 9),
        };
        this.zoomService.setLogger(this.logger, this.loggerInfo);
        if (this.isConnecting) {
            this.logger.warn(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: ======= User is joining session already =======`,
            );
            return Promise.reject(new Error("already started zoom connection"));
        }
        this.isConnecting = true;
        if (this.currentTourLive) {
            this.isConnecting = false;
            return Promise.reject(new Error("already in a tour"));
        }
        try {
            await this.zoomService.joinSession(this.userService.cu.id, tour.id);
        } catch (error) {
            this.logger.error(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: Error joining zoom ${JSON.stringify(error)}`,
            );
            this.isConnecting = false;
            return Promise.reject(error);
        }
        this.isConnecting = false;
        this.currentTourLive = tour;
        this.chatMessageService.tourStartedAt = tour.realStartedAt;
        this.chatMessageService.currentTourID = tour.id;
        // init participants after setting tour and timestamp in chatMessageService
        await this.zoomService.initParticipants();
        this.tourLaunchedEvent.next(tour);
        this.zoomUserCountSubscription =
            this.zoomService.connectedUserCount.subscribe((number) => {
                this.zoomUserCount = number;
            });
        this.zoomUserCount = this.zoomService.getZoomUserCount();
        // Photon Remote Session
        if (this.viewerService.checkMode("Matterport")) {
            const matterportService = this.viewerService.getMatterportService();
            const timer = setInterval(async () => {
                const { sdk } = matterportService;
                if (sdk) {
                    clearInterval(timer);
                    this.remoteService.setLogger(this.logger, this.loggerInfo);
                    this.remoteService.setSdk(sdk, this.currentTourLive, false);
                    await this.remoteService.connectToRoom(tour.id);
                    this.inFreeVisitMode = false;
                    this.remoteConnectionSubscription =
                        this.remoteService.onConnectionChange.subscribe(
                            (connected) => {
                                // console.log("photon connection changed", connected);
                                this.inFreeVisitMode = !connected;
                            },
                        );
                }
            }, 1000);
        }
        // subscribe to update of pseudo and avatar
        this.channelSubscription = this.API.SubscribeToChannelListener(
            tour.id,
        ).subscribe({
            next: async (data) => {
                const payload = data.value.data.subscribeToChannel.data;
                const jsonReceived = JSON.parse(payload);
                if (jsonReceived.avatar) {
                    this.pipeCacheService.cache[jsonReceived.userID].avatar =
                        jsonReceived.avatar;
                }
                if (jsonReceived.pseudo) {
                    this.pipeCacheService.cache[jsonReceived.userID].pseudo =
                        jsonReceived.pseudo;
                }
            },
            error: (error) => console.error(JSON.stringify(error)),
        });
        await this.updateConnectionToLiveTime(true);
        this.logger.info(
            `${JSON.stringify(
                this.loggerInfo,
            )}: Visitor JOINED tour successfully`,
        );
        return Promise.resolve();
    }

    async finishTour(): Promise<void> {
        // update mission template
        await this.templateService.update({
            id: this.currentTourLive.id,
            realEndedAt: DateTime.now().toMillis(),
        });
        // -- stop Photon remote session
        this.remoteService.onStopHosting();
        // --unsubscribe from channel
        if (this.channelSubscription) {
            this.channelSubscription.unsubscribe();
        }
        // --stop zoom
        return this.zoomService.leaveSession();
    }

    async leaveTourAsVisitor(): Promise<void> {
        this.tourToBeLaunchedDirectly = null;
        if (this.remoteConnectionSubscription) {
            this.remoteConnectionSubscription.unsubscribe();
        }
        if (this.guideConnectedSubscription) {
            this.guideConnectedSubscription.unsubscribe();
        }
        if (!this.inFreeVisitMode) {
            this.switchToFreeVisit();
        }

        // reset block in chat
        const currentMission = this.userService.currentMission(
            this.currentTourLive.spaceID,
        );
        if (currentMission.blockedInChat) {
            await this.missionService.update({
                id: currentMission.id,
                blockedInChat: false,
            });
        }
        return this.zoomService.leaveSession();
    }

    async leaveTour(isGuide: boolean) {
        if (isGuide) {
            this.logger.debug(
                `${JSON.stringify(this.loggerInfo)}: Leaving tour as guide`,
            );
            await this.finishTour()
                .then(() => {
                    this.tourFinishedEvent.next(true);
                    this.zoomUserCountSubscription.unsubscribe();
                    this.currentTourLive = null;
                    this.zoomUserCount = 0;
                    this.updateConnectionToLiveTime(false);
                    this.logger.info(
                        `${JSON.stringify(
                            this.loggerInfo,
                        )}: Guide LEFT tour successfully`,
                    );
                    this.logger = null;
                })
                .catch((err) => {
                    console.log(err);
                    this.logger.error(
                        `${JSON.stringify(
                            this.loggerInfo,
                        )}: Error finish tour ${JSON.stringify(err)}`,
                    );
                });
        } else {
            this.logger.debug(
                `${JSON.stringify(this.loggerInfo)}: Leaving tour as visitor`,
            );
            await this.leaveTourAsVisitor()
                .then(() => {
                    this.tourFinishedEvent.next(true);
                    this.zoomUserCountSubscription.unsubscribe();

                    this.zoomUserCount = 0;
                    this.updateConnectionToLiveTime(false);
                    this.logger.info(
                        `${JSON.stringify(
                            this.loggerInfo,
                        )}: Visitor LEFT tour successfully`,
                    );
                })
                .catch((err) => {
                    console.log(err);
                })
                .finally(() => {
                    // -- present modal of end of visit
                    const modalReference = this.modalService.open(
                        EndOfVisitModalComponent,
                        {
                            modalDialogClass: "museum-modal",
                        },
                    );
                    modalReference.componentInstance.tour =
                        this.currentTourLive;

                    // last clean
                    this.currentTourLive = null;
                    this.logger = null;
                });
        }
    }

    resetData() {
        this.zoomUserCountSubscription.unsubscribe();
        this.currentTourLive = null;
        this.zoomUserCount = 0;
    }

    switchToFreeVisit() {
        try {
            this.remoteService.leaveRoom();
        } catch (e) {
            this.logger.debug(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: Photon is disconnected already`,
            );
        }
    }

    async rejoinRemoteSessionPhoton(): Promise<void> {
        if (this.viewerService.checkMode("Matterport")) {
            const matterportService = this.viewerService.getMatterportService();
            await waitUntil(() => matterportService.sdk !== undefined);
            const { sdk } = matterportService;
            if (sdk) {
                this.remoteService.setSdk(sdk, this.currentTourLive, false);
                await this.remoteService.connectToRoom(this.currentTourLive.id);
                this.inFreeVisitMode = false;
                return Promise.resolve();
            }
            return Promise.reject();
        }
        return Promise.reject(Error("not in Matterport mode"));
    }

    initLogger() {
        this.logger = new Logger("GUIDED_TOUR", "DEBUG");
        Amplify.register(this.logger);
        this.logger.addPluggable(new AWSCloudWatchProvider());
        this.loggerInfo = { name: this.userService.cu.displayName, tourID: "" };
    }

    async updateConnectionToLiveTime(connected: boolean) {
        if (connected) {
            // update Mission with connectedToLive time
            const connectedTimes = this.currentMission.connectedToLiveAt
                ? this.currentMission.connectedToLiveAt
                : [];
            connectedTimes.push(DateTime.now().toISO());
            await this.missionService.update({
                id: this.currentMission.id,
                connectedToLiveAt: connectedTimes,
            });
        } else {
            // update Mission with connectedToLive time
            const disconnectedTimes = this.currentMission.disconnectedFromLiveAt
                ? this.currentMission.disconnectedFromLiveAt
                : [];
            disconnectedTimes.push(DateTime.now().toISO());
            await this.missionService.update({
                id: this.currentMission.id,
                disconnectedFromLiveAt: disconnectedTimes,
            });
        }
    }
}
